import { loadStripe } from '@stripe/stripe-js'
import { Controller } from '@hotwired/stimulus'

import {
  connectPageRequirements,
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import notify from '../../helpers/notify'
import { getRendererBase, getUrlParam, STRIPE_TEST_PKEY, TEST_MODE } from '../../helpers/utils'
import { computeAllTemplateValues, getCurrencySymbol } from '../../api/helpers/utils'
import jitsu from '../../helpers/jitsu'
import { debounce } from '../../helpers/debounce'
import computedValues from '../../helpers/computedValues'

import type {
  Stripe,
  StripeCheckout,
  StripeCheckoutSession,
  StripeElements,
  PaymentIntent,
  SetupIntent,
  ConfirmError,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElement,
} from '@stripe/stripe-js'
import type { Stripe as StripeTypes } from 'stripe'
import type { StripeCheckout2, UserData, TriggerError } from '../../api/models/types'
import type { EventType } from '../../api/models/analytics'

type StripeCheckoutStatus = 'fresh' | 'pending' | 'error' | 'completed'
type StripeUserDataValue = 'completed' | 'failed' | 'pending'

type FlowContext = {
  flowId: string | null
  component: StripeCheckout2
  flowVersion: string | null
  pageId: string
}

type StripeApiRequest = StripeApiRequestCreateCheckoutSession | StripeApiRequestGetCheckoutSession

type StripeApiRequestCreateCheckoutSession = {
  flow_id: string | null
  flow_version: string | null
  component_id: string
  env: 'dev' | 'prod'
  userData: UserData
  origin_url: string
}

type StripeApiRequestGetCheckoutSession = {
  session_id: string
  flow_id: string
  env: 'dev' | 'prod'
}

type StripeApiResponse =
  | StripeApiResponseCreateCheckoutSession
  | StripeApiResponseGetCheckoutSession

type StripeApiResponseCreateCheckoutSession = {
  completed: boolean
  error?: string
  statusCode?: number
  customer?: StripeTypes.Customer
  payment_intents?: PaymentIntent[]
  setup_intents?: SetupIntent[]
  subscriptions?: StripeTypes.Subscription[]
  checkoutSessionClientSecret?: string
  omitConfirmCheckoutEmail?: boolean
}

//* Session object returned from get-checkout-session (Stripe Checkout.Session serialized)
type StripeCheckoutSessionFromApi = {
  id?: string
  status?: 'complete' | 'expired' | 'open'
  client_secret?: string | null
  payment_intent?: string
  subscription?: string
  setup_intent?: string
  [key: string]: unknown
}

type StripeApiResponseGetCheckoutSession = {
  session?: StripeCheckoutSessionFromApi
  error?: string
  details?: string
}

//* Shared checkout session coordination.
//* When multiple StripeCheckout2 components on the same page have use_shared_stripe_session,
//* they share one Stripe Checkout session so coupons and discounts apply to all line items.
type SharedCheckoutEntry = {
  checkoutPromise: Promise<StripeCheckout>
  componentIds: string[]
  /** Set when the shared checkout promise resolves — used for debug logs on invalidate */
  sessionId?: string
}

const sharedCheckoutSessions = new Map<string, SharedCheckoutEntry>()

function createDeferredStripeCheckout(): {
  promise: Promise<StripeCheckout>
  resolve: (value: StripeCheckout) => void
  reject: (reason?: unknown) => void
} {
  let resolveCheckout!: (value: StripeCheckout) => void
  let rejectCheckout!: (reason?: unknown) => void
  const promise = new Promise<StripeCheckout>((resolve, reject) => {
    resolveCheckout = resolve
    rejectCheckout = reject
  })
  return { promise, resolve: resolveCheckout, reject: rejectCheckout }
}

function getSharedSessionKey({
  flowId,
  pageId,
}: {
  flowId: string | null
  pageId: string
}): string {
  return `${flowId || 'unknown'}_${pageId}`
}

function invalidateSharedSession({
  flowId,
  pageId,
}: {
  flowId: string | null
  pageId: string
}): void {
  const key = getSharedSessionKey({ flowId, pageId })
  const entry = sharedCheckoutSessions.get(key)
  const sessionId = entry?.sessionId
  const hadEntry = sharedCheckoutSessions.delete(key)
  console.log('[StripeCheckout2] shared session map invalidated', { key, hadEntry, sessionId })
}

export default class extends Controller {
  static targets = [
    'card',
    'expressCheckout',
    'button',
    'buttonContentPending',
    'buttonContentPay',
    'buttonContentPayText',
    'errorContainer',
    'successContainer',
  ]

  declare cardTarget: HTMLElement
  declare hasCardTarget: boolean
  declare expressCheckoutTarget: HTMLElement
  declare hasExpressCheckoutTarget: boolean
  declare buttonTarget: HTMLElement
  declare hasButtonTarget: boolean
  declare buttonContentPendingTarget: HTMLElement
  declare hasButtonContentPendingTarget: boolean
  declare buttonContentPayTarget: HTMLElement
  declare hasButtonContentPayTarget: boolean
  declare buttonContentPayTextTarget: HTMLElement
  declare hasButtonContentPayTextTarget: boolean
  declare errorContainerTarget: HTMLElement | null
  declare hasErrorContainerTarget: boolean | string
  declare successContainerTarget: HTMLElement
  declare hasSuccessContainerTarget: boolean

  static values = {
    isValid: Boolean,
  }
  declare isValidValue: true

  disconnectPageRequirements?: () => void
  disconnectUserData?: () => void
  flowContext: FlowContext
  declare userDataValue: UserData
  status: StripeCheckoutStatus
  component: StripeCheckout2
  stripe: Stripe
  checkout: StripeCheckout | null
  stripeElements: StripeElements
  currentComponentConfig: StripeCheckout2
  private omitConfirmCheckoutEmail = false
  private expressCheckoutElementForIntercept: StripeExpressCheckoutElement | null = null
  private debouncedSetUpStripe: ((userData: UserData) => void) | null = null
  private boundOnPageShow = (event: PageTransitionEvent) => this.onPageShow(event)
  //* Tracks the checkout instance this component has attached listeners to, preventing duplicates
  private listeningToCheckout: StripeCheckout | null = null
  //* Guards against stale debounced setUpStripe calls that fire after disconnect
  private isDisconnected = true
  //* Incremented each time setUpStripe starts; used to abandon superseded async calls
  private setupGeneration = 0

  connect() {
    this.isDisconnected = false
    this.disconnectPageRequirements = connectPageRequirements(this)
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.flowContext = getComponentContext(this) as unknown as FlowContext
    this.component = this.flowContext.component

    //* Invalidate any stale shared session left over from a previous page visit.
    //* On a full reload the module-level Map is naturally empty; during internal
    //* (SPA) navigation the old entry survives because disconnect() can't always
    //* reach the DOM.  All sibling connect() calls run synchronously before any
    //* async setUpStripe work, so every sibling clears here first, then the first
    //* setUpCheckoutSession creates a fresh entry that later siblings join.
    if (this.component.use_shared_stripe_session === true) {
      invalidateSharedSession(this.flowContext)
    }

    //* Apply initial button disabled state — connectPageRequirements fires isValidValueChanged
    //* before this.component is set, so we re-apply the state here once we have all context.
    if (
      this.component?.disable_button_until_page_valid &&
      !this.isValidValue &&
      this.hasButtonTarget
    ) {
      this.buttonTarget.setAttribute('disabled', 'disabled')
    }

    // Create debounced version of setUpStripe
    this.debouncedSetUpStripe = debounce(this.setUpStripe.bind(this), 300)

    if (missingRequiredHttps()) {
      this.setStatus('error', {
        code: 'https_required',
        message:
          'To make things secure, we only accept payments via HTTPS, not HTTP - please reload this using HTTPS.',
      })
    }

    //* Returning from redirect (e.g. Klarna, Amazon Pay): use session_id from URL to determine outcome
    const redirectSessionId = getUrlParam('session_id')
    //TODO: remove the !this.component.checkout_session.return_url check once the team migrated the flow with workaround.
    if (redirectSessionId && !this.component.checkout_session.return_url) {
      //* Void the promise to avoid blocking the main thread
      void this.handleRedirectReturn(redirectSessionId)
      return
    }

    this.setUpStripe(this.userDataValue).then(() => {
      lifecycleTrigger(this, 'mounted')
    })

    //* Set initial status if user data already has a card or payment
    if ((this.userDataValue?.[this.component.key] as StripeUserDataValue) === 'completed') {
      this.setStatus('completed')
    }

    //* Listen for pageshow event to handle back/forward cache (bfcache) restoration
    window.addEventListener('pageshow', this.boundOnPageShow)
  }

  onPageShow(event: PageTransitionEvent) {
    console.log('onPageShow', event)
    //* event.persisted is true when the page was restored from bfcache
    if (event.persisted) {
      this.onBFCacheRestore()
    }
  }

  //* Called when the page is restored from bfcache (e.g. browser back button from Klarna redirect).
  onBFCacheRestore() {
    //* Reset the button from "processing" back to the pay state so the user can interact again
    this.setStatus('fresh')
    //* Invalidate the cached config so setUpStripe doesn't skip due to the JSON.stringify equality check
    this.currentComponentConfig = null as any
    //* Invalidate shared session so a fresh one is created
    if (this.isSharedSessionMode()) {
      invalidateSharedSession(this.flowContext)
    }
    this.listeningToCheckout = null
    //* Re-create the checkout session, remount Stripe elements, and restore a working confirm() flow
    this.setUpStripe(this.userDataValue)
  }

  disconnect() {
    this.isDisconnected = true
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (typeof this.disconnectPageRequirements === 'function') this.disconnectPageRequirements()
    //* Clean up intercept mode express checkout element
    this.cleanUpExpressCheckoutIntercept()

    if (this.checkout) {
      const stripeVersion = detectStripeVersion()
      if (stripeVersion === 'basil') {
        this.checkout.getPaymentElement()?.unmount()
        this.checkout.getExpressCheckoutElement()?.unmount()
      } else {
        // v3 API
        ;(this.checkout as any).getElement('payment')?.unmount()
        ;(this.checkout as any).getElement('expressCheckout')?.unmount()
      }
    }
    //* Remove from the ordered StripeCheckouts array
    if (this.component?.id) unregisterStripeCheckout(this.component.id)
    //* Invalidate shared session when this component disconnects.
    //* We check use_shared_stripe_session directly instead of isSharedSessionMode()
    //* because during disconnect the element may already be detached from the DOM,
    //* causing getFlow() → element.closest() to return null.
    if (this.component?.use_shared_stripe_session === true && this.flowContext) {
      invalidateSharedSession(this.flowContext)
      //* Reset shared-session state so the next connect() starts clean
      this.checkout = null
      this.currentComponentConfig = null as unknown as StripeCheckout2
    }
    this.listeningToCheckout = null
    //* Clear the debounced function
    this.debouncedSetUpStripe = null
    //* Remove pageshow listener
    window.removeEventListener('pageshow', this.boundOnPageShow)
  }

  userDataValueChanged(newData: UserData) {
    if (!this.component) return

    //* The updated user data may have changed the component config
    //* So we need to re-evaluate the component config and update the Stripe instance if necessary
    if (this.debouncedSetUpStripe) {
      this.debouncedSetUpStripe(newData)
    }
  }

  //* Called by Stimulus whenever the page validity changes (synced by connectPageRequirements).
  //* When disable_button_until_page_valid is on, keep the pay button in sync with page validity
  //* while still respecting the existing pending/completed status logic.
  isValidValueChanged() {
    if (!this.component?.disable_button_until_page_valid) return
    if (!this.hasButtonTarget) return
    this.shouldAllowAttemptPayment()
      ? this.buttonTarget.removeAttribute('disabled')
      : this.buttonTarget.setAttribute('disabled', 'disabled')
  }

  async setUpStripe(userData: UserData) {
    if (this.isDisconnected) return
    //* For shared-session components, claim a generation number so any earlier
    //* async call still in flight can detect it has been superseded and bail out.
    //* Non-shared components skip this to avoid any behavioural change.
    const isSharedStripeSession = this.component?.use_shared_stripe_session === true
    if (isSharedStripeSession) ++this.setupGeneration
    const generation = this.setupGeneration

    //* Calculate all component config data with new User Data
    //* If it's not changed, or if it's invalid, then stop here
    const newComponentConfig = {
      ...computeAllTemplateValues(this.component, userData),
    } as StripeCheckout2
    const currentComponentConfig = this.currentComponentConfig || {}

    const configChanged =
      JSON.stringify(getConfigWithInitialPropsOnly(newComponentConfig)) !==
      JSON.stringify(getConfigWithInitialPropsOnly(currentComponentConfig))

    const isSharedMode = this.isSharedSessionMode()
    //* True only on the very first setUpStripe call for this controller instance.
    //* On first setup currentComponentConfig has not been assigned yet, so the
    //* JSON comparison always reports configChanged even when nothing meaningful
    //* changed.  Invalidating the shared map entry at that point would race
    //* against a sibling that already reserved the slot, causing a second
    //* create-checkout-session request.
    const isFirstSetup = !this.currentComponentConfig
    //* Shared-mode invalidation is handled atomically inside setUpCheckoutSession,
    //* right before the new map entry is reserved.  Doing it here was the root cause
    //* of the multi-session race: when two siblings' debounces fire in the same JS
    //* turn, the second one would wipe the fresh pending deferred the first had just
    //* written, so both ended up creating their own independent sessions.

    //* In non-shared mode, skip if config hasn't changed
    if (!configChanged) {
      //* In shared mode, still check if shared session exists (a sibling may have invalidated it)
      if (!isSharedMode) return
      const key = getSharedSessionKey(this.flowContext)
      const existing = sharedCheckoutSessions.get(key)
      //* Must match the map's current checkout — if a sibling recreated the session, listeningToCheckout
      //* can still point at a stale instance and we'd skip registerStripeCheckout (window.StripeCheckouts out of sync).
      if (existing) {
        const sharedCheckout = await existing.checkoutPromise
        if (this.checkout === sharedCheckout && this.listeningToCheckout === sharedCheckout) {
          return
        }
      }
    }

    this.currentComponentConfig = newComponentConfig

    //* Intercept mode: render Express Checkout buttons via Elements API (Payment Intents)
    //* and intercept clicks instead of opening the native payment sheet
    if (this.isExpressCheckoutInterceptMode()) {
      //* Clean up normal mode resources if they exist (handles runtime mode switching)
      if (this.checkout) {
        const stripeVersion = detectStripeVersion()

        //* If the Stripe version is not Basil, throw an error
        if (stripeVersion !== 'basil') {
          throw new Error('Express checkout is not supported in this version of Stripe')
        }
        this.checkout.getExpressCheckoutElement()?.unmount()
        this.checkout = null as any
      }

      this.stripe = await getStripe(this.element)
      this.setUpExpressCheckoutInterceptMode()
      return
    }

    //* Clean up intercept mode resources if they exist (handles runtime mode switching)
    this.cleanUpExpressCheckoutIntercept()

    if (
      !newComponentConfig.checkout_session.mode ||
      (newComponentConfig.checkout_session.mode !== 'setup' &&
        !newComponentConfig.checkout_session.line_items?.length)
    ) {
      console.warn('Invalid Stripe Checkout component config')
      return
    }

    //* Get Stripe instance
    this.stripe = await getStripe(this.element)
    if (isSharedStripeSession && generation !== this.setupGeneration) return

    //* Create a new checkout session (or reuse shared session when siblings exist)
    const newCheckout = await this.setUpCheckoutSession({ configChanged, isFirstSetup })
    //* Bail out if a newer setUpStripe call has superseded this one
    //* (e.g. updateFlowUserData fired with new stripe_line_items while we were awaiting)
    if (isSharedStripeSession && generation !== this.setupGeneration) return
    this.checkout = newCheckout
    window.StripeCheckout = this.checkout

    //* Set up the payment element if it's required
    if (this.component.elements_to_display.includes('payment')) this.setUpPaymentElement()

    //* Set up the express checkout element if it's required
    if (this.component.elements_to_display.includes('express_checkout')) this.setUpExpressCheckout()

    //* Set the button text with the correct currency symbol and total price (or custom text)
    //* Only when the payment element is shown and express checkout is not (express has its own buttons)
    if (this.shouldUpdatePayButtonText()) this.setButtonText()

    //* Update the checkout total in user data
    this.updateCheckoutTotalInUserData(this, this.checkout.session())

    //* Register in the ordered StripeCheckouts array
    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.flowContext.pageId) || 0
    const componentIndex =
      flow?.pages[pageIndex]?.components.findIndex(
        (component) => component.id === this.component.id
      ) || 0
    registerStripeCheckout(this.component.id, componentIndex, this.checkout)
  }

  async setUpCheckoutSession({
    configChanged,
    isFirstSetup,
  }: {
    configChanged: boolean
    isFirstSetup: boolean
  }): Promise<StripeCheckout> {
    if (this.isSharedSessionMode()) {
      const key = getSharedSessionKey(this.flowContext)
      let existing = sharedCheckoutSessions.get(key)

      if (existing) {
        //* On first setup (fresh instance after connect), the entry was created by a
        //* sibling in the same cycle — connect() already cleared stale entries first,
        //* so we always join the fresh one regardless of sessionId.
        //*
        //* On subsequent calls (userDataValueChanged with a config change), we must
        //* force a new session.  We also invalidate when sessionId is still undefined
        //* (session API call in-flight with stale data) because the setupGeneration
        //* guard in setUpStripe will abandon that in-flight call after its await.
        if (configChanged && !isFirstSetup) {
          invalidateSharedSession({
            flowId: this.flowContext.flowId,
            pageId: this.flowContext.pageId,
          })
          //* Fall through to create a new session below
        } else {
          return this.reuseOrAwaitSharedCheckout({ key, entry: existing })
        }
      }

      //* First component to arrive creates the shared session — validate compatibility first
      this.assertSharedSessionCompatibility()

      //* Double-check: another controller may have registered a pending promise while we validated
      existing = sharedCheckoutSessions.get(key)
      if (existing) {
        return this.reuseOrAwaitSharedCheckout({ key, entry: existing })
      }

      //* Reserve the map slot synchronously with a pending promise BEFORE createOwnCheckoutSession
      //* runs any await — otherwise two controllers can both pass the first get() and start two APIs.
      const deferred = createDeferredStripeCheckout()
      const sharedEntry: SharedCheckoutEntry = {
        checkoutPromise: deferred.promise,
        componentIds: this.getSharedStripeSessionCohortComponentIds(),
      }
      sharedCheckoutSessions.set(key, sharedEntry)

      void this.createOwnCheckoutSession()
        .then((resolvedCheckout) => {
          const stored = sharedCheckoutSessions.get(key)
          if (stored) {
            stored.sessionId = resolvedCheckout.session()?.id
          }
          deferred.resolve(resolvedCheckout)
        })
        .catch((error: unknown) => {
          sharedCheckoutSessions.delete(key)
          deferred.reject(error)
        })

      //* setUpCheckoutSession creating new shared checkout
      const checkout = await deferred.promise
      this.attachCheckoutChangeListeners(checkout)
      return checkout
    }

    //* Single component on the page: original behavior
    const checkout = await this.createOwnCheckoutSession()
    return checkout
  }

  private async createOwnCheckoutSession(): Promise<StripeCheckout> {
    const response = (await callPaymentsBackend('create-checkout-session', {
      flow_id: this.flowContext.flowId,
      flow_version: this.flowContext.flowVersion,
      component_id: this.component.id,
      env: TEST_MODE ? 'dev' : 'prod',
      userData: this.userDataValue,
      origin_url: window.location.href,
    })) as StripeApiResponseCreateCheckoutSession

    if (response.error) throw new Error(`Error creating checkout session: ${response.error}`)

    const clientSecret = response.checkoutSessionClientSecret
    if (!clientSecret) throw new Error('No client secret returned from backend')
    this.omitConfirmCheckoutEmail = response.omitConfirmCheckoutEmail === true

    const checkout = await this.initCheckoutWithClientSecret(clientSecret)

    const self = this
    checkout.on('change', function (session: StripeCheckoutSession) {
      console.log('Stripe Checkout session updated', session, {
        sessionId: session?.id,
      })
      //* Update the button text with the latest total price when applicable
      if (self.shouldUpdatePayButtonText()) self.setButtonText()
      //* Update the checkout total in user data
      self.updateCheckoutTotalInUserData(self, session)
    })

    return checkout
  }

  private async reuseOrAwaitSharedCheckout({
    key,
    entry,
  }: {
    key: string
    entry: SharedCheckoutEntry
  }): Promise<StripeCheckout> {
    const checkout = await entry.checkoutPromise
    const sessionId = checkout.session()?.id
    console.log('[StripeCheckout2] setUpCheckoutSession reusing shared checkout', {
      componentId: this.component.id,
      key,
      cohortComponentIds: entry.componentIds,
      sessionId,
    })
    this.attachCheckoutChangeListeners(checkout)
    return checkout
  }

  //* Guards against duplicate listeners when reusing a shared checkout
  private attachCheckoutChangeListeners(checkout: StripeCheckout): void {
    if (this.listeningToCheckout === checkout) return
    this.listeningToCheckout = checkout

    const self = this
    checkout.on('change', function (session: StripeCheckoutSession) {
      if (self.shouldUpdatePayButtonText()) self.setButtonText()
      self.updateCheckoutTotalInUserData(self, session)
    })
  }

  //* StripeCheckout2 components on the same page that opt in to use_shared_stripe_session
  private getSharedStripeSessionCohortComponents(): StripeCheckout2[] {
    const flow = getFlow(this, true)
    if (!flow) {
      return this.component.use_shared_stripe_session === true ? [this.component] : []
    }

    const page = flow.pages?.find((p: { id: string }) => p.id === this.flowContext.pageId)
    if (!page) {
      return this.component.use_shared_stripe_session === true ? [this.component] : []
    }

    return page.components.filter((c: { type: string; id: string }) => {
      if (c.type !== 'StripeCheckout2') return false
      return (c as StripeCheckout2).use_shared_stripe_session === true
    }) as StripeCheckout2[]
  }

  private getSharedStripeSessionCohortComponentIds(): string[] {
    return this.getSharedStripeSessionCohortComponents().map((c: { id: string }) => c.id)
  }

  //* Multiple opt-in components on the same page — they share one Stripe session
  private isSharedSessionMode(): boolean {
    return (
      this.component.use_shared_stripe_session === true &&
      this.getSharedStripeSessionCohortComponentIds().length > 1
    )
  }

  //* Validates that all cohort StripeCheckout2 components share the same checkout_session
  //* mode and currency — mismatching values would produce an invalid shared Stripe session
  private assertSharedSessionCompatibility(): void {
    const siblings = this.getSharedStripeSessionCohortComponents()
    const referenceMode = this.currentComponentConfig?.checkout_session?.mode
    const referenceCurrency = this.currentComponentConfig?.checkout_session?.currency

    for (const sibling of siblings) {
      if (sibling.id === this.component.id) continue

      const siblingMode = sibling.checkout_session?.mode
      const siblingCurrency = sibling.checkout_session?.currency

      if (siblingMode && referenceMode && siblingMode !== referenceMode) {
        throw new Error(
          `Shared checkout session error: component "${this.component.id}" has mode "${referenceMode}" but cohort component "${sibling.id}" has mode "${siblingMode}". All StripeCheckout2 components with use_shared_stripe_session on the same page must share the same mode.`
        )
      }

      if (siblingCurrency && referenceCurrency && siblingCurrency !== referenceCurrency) {
        throw new Error(
          `Shared checkout session error: component "${this.component.id}" has currency "${referenceCurrency}" but cohort component "${sibling.id}" has currency "${siblingCurrency}". All StripeCheckout2 components with use_shared_stripe_session on the same page must share the same currency.`
        )
      }
    }
  }

  async setUpPaymentElement() {
    if (!this.checkout) throw new Error('Checkout is not initialized')
    if (!this.hasCardTarget) throw new Error('Card target is required')
    if (!this.hasButtonTarget) throw new Error('Button target is required')
    if (!this.hasButtonContentPendingTarget)
      throw new Error('Button content pending target is required')
    if (!this.hasButtonContentPayTarget) throw new Error('Button content pay target is required')
    if (!this.hasErrorContainerTarget) throw new Error('Error container target is required')
    if (!this.hasSuccessContainerTarget) throw new Error('Success container target is required')

    const stripeVersion = detectStripeVersion()
    if (stripeVersion !== 'basil') {
      throw new Error('Payment element is not supported in this version of Stripe')
    }

    //* In shared mode, the payment element may already exist from a sibling component
    const existingPaymentElement = this.checkout.getPaymentElement?.()
    if (existingPaymentElement) {
      console.warn(
        'Shared session: payment element already created by a sibling component — skipping mount'
      )
      return
    }

    const createElementOptions = computeAllTemplateValues(
      this.component.create_element_options || {},
      this.userDataValue
    )

    const paymentElement = this.checkout.createPaymentElement({
      layout: 'tabs',
      ...createElementOptions,
    })
    paymentElement.mount(this.cardTarget)
  }

  async setUpExpressCheckout() {
    if (!this.checkout) throw new Error('Checkout is not initialized')
    if (!this.hasExpressCheckoutTarget) throw new Error('Express checkout target is required')

    const stripeVersion = detectStripeVersion()
    if (stripeVersion !== 'basil') {
      throw new Error('Express checkout is not supported in this version of Stripe')
    }

    //* In shared mode, the express checkout element may already exist from a sibling component
    const existingExpressCheckout = this.checkout.getExpressCheckoutElement?.()
    if (existingExpressCheckout) {
      console.warn(
        'Shared session: express checkout element already created by a sibling component — skipping mount'
      )
      return
    }

    const createExpressCheckoutElementOptions = computeAllTemplateValues(
      this.component.create_express_checkout_element_options || {},
      this.userDataValue
    )

    const expressCheckout = this.checkout.createExpressCheckoutElement({
      buttonHeight: 40,
      buttonTheme: {},
      buttonType: {},
      layout: {},
      paymentMethodOrder: [],
      paymentMethods: {},
      ...createExpressCheckoutElementOptions,
    })

    expressCheckout.mount(this.expressCheckoutTarget)

    const self = this
    expressCheckout.on('confirm', (event) => {
      //* Attempt to process the payment
      self.attemptPayment({ expressCheckoutConfirmEvent: event })
    })
  }

  //* Returns true when the component should intercept Express Checkout clicks
  //* instead of using the normal Checkout Sessions flow
  private isExpressCheckoutInterceptMode(): boolean {
    const elements = this.component.elements_to_display
    const onlyExpressCheckout = elements.length === 1 && elements.includes('express_checkout')
    return onlyExpressCheckout && this.component.prevent_default_click_action === true
  }

  //* Sets up Express Checkout in intercept mode using the Elements API (deferred Payment Intents).
  //* No Checkout Session is created. Clicks are intercepted and dispatched as custom actions.
  private setUpExpressCheckoutInterceptMode() {
    if (!this.hasExpressCheckoutTarget) throw new Error('Express checkout target is required')

    //* Clean up any existing intercept mode element
    this.cleanUpExpressCheckoutIntercept()

    const createExpressCheckoutElementOptions = computeAllTemplateValues(
      this.component.create_express_checkout_element_options || {},
      this.userDataValue
    )

    //* Derive currency and mode from the existing checkout_session config
    const currency = this.component.checkout_session.currency || 'usd'
    const mode = this.component.checkout_session.mode === 'setup' ? 'setup' : 'payment'

    //* Create a Stripe Elements instance in deferred intent mode (no clientSecret, no server call)
    const elementsOptions: {
      mode: 'payment' | 'setup'
      currency: string
      amount?: number
    } = {
      mode,
      currency,
    }

    //* amount is required for 'payment' mode but not 'setup' mode.
    //* Since the payment sheet never opens in intercept mode, this is a placeholder value.
    if (mode === 'payment') {
      elementsOptions.amount = 100
    }

    this.stripeElements = this.stripe.elements(elementsOptions)

    //* Create Express Checkout Element via Elements API (NOT Checkout Sessions API)
    //* This gives us a 'click' event we can intercept before the payment sheet opens
    const expressCheckout = this.stripeElements.create('expressCheckout', {
      buttonHeight: 40,
      buttonTheme: {},
      buttonType: {},
      layout: {},
      paymentMethodOrder: [],
      paymentMethods: {},
      ...createExpressCheckoutElementOptions,
    })

    expressCheckout.mount(this.expressCheckoutTarget)
    this.expressCheckoutElementForIntercept = expressCheckout

    //* Listen for click event - this fires BEFORE the payment sheet opens
    const self = this
    expressCheckout.on('click', (event: StripeExpressCheckoutElementClickEvent) => {
      //* Reject immediately to dismiss any payment interface loading state
      event.reject()

      //* Dispatch custom action for the parent flow to handle
      self.dispatchExpressCheckoutClickIntercepted({ expressPaymentType: event.expressPaymentType })
    })
  }

  private cleanUpExpressCheckoutIntercept() {
    if (this.expressCheckoutElementForIntercept) {
      this.expressCheckoutElementForIntercept.unmount()
      this.expressCheckoutElementForIntercept = null
    }
  }

  //* Dispatches the intercepted Express Checkout click via on_payment_button_click_outputs
  private dispatchExpressCheckoutClickIntercepted({
    expressPaymentType,
  }: {
    expressPaymentType: string
  }) {
    const component = this.flowContext.component
    const actions = component.on_payment_button_click_outputs

    if (Array.isArray(actions)) {
      //* Dispatch the actions
      this.dispatch('actions', {
        detail: {
          actions,
          component: this.component,
          expressPaymentType,
        },
      })
    }
  }

  async attemptPayment({
    expressCheckoutConfirmEvent,
  }: {
    expressCheckoutConfirmEvent?: StripeExpressCheckoutElementConfirmEvent
  }) {
    try {
      if (!this.checkout) throw new Error('Checkout is not initialized')

      //* First, run regular page validation if enforce_validation is true
      //* If invalid, trigger the regular validation procedure and stop here
      if (this.component.enforce_validation && !this.isValidValue) {
        this.dispatch('validate')
        this.setStatus('fresh')
        return
      }

      //* If the payment is not allowed, stop here
      if (!this.shouldAllowAttemptPayment()) return
      if (missingRequiredHttps()) throw new Error('Stripe requires HTTPS')

      this.triggerChangeOfStatus('pending')

      if (TEST_MODE && window.location.search.includes('force_stripe_error'))
        throw new Error('An error occurred while processing the payment')

      //* Run the final Stripe Elements confirm step
      const confirmOptions = {
        ...computeAllTemplateValues(this.component.confirm_checkout_options || {}, this.userDataValue),
      }
      if (this.omitConfirmCheckoutEmail) {
        delete confirmOptions.email
      }

      let returnUrl: string | undefined = window.location.href
      //* Use currentComponentConfig (resolved template values) to correctly detect dynamic payment_method_types
      const resolvedPaymentMethodTypes = this.currentComponentConfig.checkout_session.payment_method_types
      const resolvedReturnUrl = this.currentComponentConfig.checkout_session.return_url
      //* If the payment method is Klarna or the return url is set, we need to set the returnUrl as undefined because the checkout session has a return_url set
      if (resolvedPaymentMethodTypes?.includes('klarna') || resolvedReturnUrl) {
        returnUrl = undefined
      }

      let options = {
        returnUrl,
        redirect: 'if_required',
        ...confirmOptions,
      }

      //* If the express checkout confirm event is provided, use it
      if (expressCheckoutConfirmEvent) {
        options = {
          ...options,
          expressCheckoutConfirmEvent,
        }
      }

      //* Confirm the payment
      const res = await this.checkout.confirm(options)

      //* Handle errors returned from the confirm step
      if (res.type === 'error') {
        const error = res.error
        console.warn(error)
        return this.triggerChangeOfStatus('error', {
          code:
            (error.code === 'paymentFailed' && error.paymentFailed?.declineCode) ||
            error.code ||
            'error',
          message: error.message || 'An error occurred while processing the payment',
        })
      }

      //* Save the outputs to user data, waiting for that to complete before triggering the completed status
      if (this.component.save_outputs_to_user_data) await this.saveOutputsToUserData()

      //* Trigger the completed status
      this.triggerChangeOfStatus('completed')
    } catch (error) {
      console.error(error)
      this.triggerChangeOfStatus('error', {
        code: error.paymentFailed?.declineCode || error?.code || 'error',
        message: error?.message || 'An error occurred while processing the payment',
      })
      this.notifyEmbeddablesTeamOfError(error)
    }
  }

  //* HELPER FUNCTIONS

  //* Handle return from redirect (e.g. Klarna, Amazon Pay).
  //* Polls resolveRedirectPaymentStatus until we get a definitive result (this.checkout.session() + paymentStatus).
  async handleRedirectReturn(sessionId: string): Promise<void> {
    if (!this.flowContext.flowId) {
      this.triggerChangeOfStatus('error', {
        code: 'missing_flow',
        message: 'Could not verify payment (missing flow context).',
      })
      return
    }

    const maxPollAttempts = 15

    try {
      let result: true | false | 'unpaid' | undefined
      let session: StripeCheckoutSessionFromApi | null = null
      let attempts = 0

      //* Trigger the pending status
      this.triggerChangeOfStatus('pending')
      while (attempts < maxPollAttempts) {
        //* Resolve the redirect payment status
        const resolved = await this.resolveRedirectPaymentStatus(sessionId, this.flowContext.flowId)
        //* Get the result
        result = resolved.result
        //* Get the session
        session = resolved.session

        //* If the result is not undefined, break the loop
        if (result !== undefined) break
        //* Increment the attempts
        attempts++
        //* Wait for attempts * 1 second
        await new Promise((r) => setTimeout(r, attempts * 1000))
      }

      if (result === true) {
        //* Save the outputs to user data if the component has save_outputs_to_user_data and the session is not null
        if (this.component.save_outputs_to_user_data && session) {
          await this.saveOutputsToUserDataFromSession(session)
        }
        //* Trigger the completed status
        this.triggerChangeOfStatus('completed')
      } else if (result === false) {
        //* Trigger the error status
        this.triggerChangeOfStatus('error', {
          code: 'session_expired',
          message: 'This checkout session has expired.',
        })
      } else if (result === 'unpaid') {
        //* Trigger the error status
        this.triggerChangeOfStatus('error', {
          code: 'payment_not_completed',
          message: 'Payment was not completed.',
        })
      } else {
        throw new Error('Could not verify payment.')
      }

      //* Clean the redirect params
      cleanRedirectParams()
    } catch (error) {
      this.triggerChangeOfStatus('error', {
        code: 'redirect_handling_failed',
        message: error instanceof Error ? error.message : 'Could not verify payment.',
      })
      cleanRedirectParams()
    } finally {
      lifecycleTrigger(this, 'mounted')
    }
  }

  //* Resolve redirect outcome: fetches session once, then verifies via this.checkout.session() + paymentStatus.
  //* Returns result undefined when session is still open (caller should poll). Returns session for saveOutputsToUserData when result === true.
  private async resolveRedirectPaymentStatus(
    sessionId: string,
    flowId: string
  ): Promise<{
    result: true | false | 'unpaid' | undefined
    session: StripeCheckoutSessionFromApi | null
  }> {
    //* Fetch the session from the backend
    const response = await fetchSession({ sessionId, flowId })
    if (response.error || !response.session) {
      throw new Error(response.error || response.details || 'Could not verify payment.')
    }

    //* Get the session
    const session = response.session

    //* If the session is expired, return false
    if (session.status === 'expired') return { result: false, session }
    //* If the session is open, return undefined
    if (session.status === 'open') return { result: undefined, session }

    //* When we have client_secret, use this.checkout.session() for authoritative paymentStatus
    if (session.client_secret) {
      try {
        //* Get the Stripe instance
        this.stripe = await getStripe(this.element)
        //* Initialize the checkout session with the client secret
        this.checkout = await this.initCheckoutWithClientSecret(session.client_secret)
        //* Get the session
        const sdkSession = this.checkout.session()
        //* Get the status
        const status = sdkSession.status

        //* If the session is complete, return true if the payment status is paid, otherwise return 'unpaid'
        if (status.type === 'complete') {
          const result: true | 'unpaid' = status.paymentStatus === 'paid' ? true : 'unpaid'
          return { result, session }
        }
        //* If the session is expired, return false
        if (status.type === 'expired') return { result: false, session }
        //* If the session is open, return undefined
        return { result: undefined, session }
      } catch {
        //* Fall through to API session status when SDK init fails
      }
    }

    //* Fallback: use API session status only (no paymentStatus check)
    const result: true | false = session.status === 'complete' ? true : false
    return { result, session }
  }

  //* Init checkout with an existing session client_secret (e.g. after redirect). No UI elements.
  private async initCheckoutWithClientSecret(clientSecret: string): Promise<StripeCheckout> {
    const initCheckoutOptions = computeAllTemplateValues(
      this.component.init_checkout_options || {},
      this.userDataValue
    )
    //* Initialize the checkout session
    const stripeVersion = detectStripeVersion()
    if (stripeVersion === 'basil') {
      return this.stripe.initCheckout({
        fetchClientSecret: () => clientSecret,
        ...initCheckoutOptions,
      })
    }
    //* Stripe v3 API
    return (this.stripe as any).initCheckout({
      clientSecret,
      ...initCheckoutOptions,
    })
  }

  //* Triggers a change of status, sends data updates, and triggers relevant actions etc
  //* For setting the initial status onload, use setStatus() directly instead of this
  triggerChangeOfStatus(status: StripeCheckoutStatus, error?: TriggerError) {
    this.setStatus(status, error)

    if (status === 'fresh') return

    const component = this.flowContext.component

    //* Update the value in user data
    this.dispatch('update', { detail: { key: component.key, value: status } })

    //* Trigger relevant actions
    const actionKey = statusToActionKey(status)
    const actions = component[actionKey]

    if (Array.isArray(actions)) {
      const dispatchDetail: {
        actions: string[]
        component: StripeCheckout2
        triggerError?: TriggerError
      } = { actions, component: this.component }
      if (status === 'error') {
        dispatchDetail.triggerError = error
      }
      this.dispatch('actions', { detail: dispatchDetail })
    }

    //* Send events to analytics
    const analyticsEventName = statusToAnalyticsEvent(status)
    if (!doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track(analyticsEventName, {
        flow_id: this.flowContext.flowId || '',
        payment_service: 'stripe',
      })
    }

    //* Log events for debugging
    this.addEventLog(analyticsEventName)

    //* If completed, then trigger next page and conversion events
    if (status === 'completed') {
      if (component.next_on_complete) {
        this.dispatch('next', { detail: { value: this.flowContext.pageId } })
      }

      if (
        component.on_payment_complete_conversions &&
        Array.isArray(component.on_payment_complete_conversions)
      ) {
        this.dispatch('actions', {
          detail: {
            actions: component.on_payment_complete_conversions.map((c) => ({
              ...c,
              output: 'conversion',
            })),
            component: this.component,
          },
        })
      }
    }
  }

  setStatus(status: StripeCheckoutStatus, error?: TriggerError) {
    console.log(
      `Setting status to ${status}${error?.message ? ` with message: "${error.message}"` : ''}`
    )

    const hasButton = this.hasButtonTarget

    //* Set local status variable
    this.status = status

    //* Disable/enable button based on status
    if (hasButton) {
      this.shouldAllowAttemptPayment()
        ? this.buttonTarget.removeAttribute('disabled')
        : this.buttonTarget.setAttribute('disabled', 'disabled')
    }

    //* Clear any existing messages
    this.setFeedbackMessage('error', null)
    this.setFeedbackMessage('completed', null)

    //* Reset all button content
    if (hasButton) {
      this.buttonContentPendingTarget.style.display = 'none'
      this.buttonContentPayTarget.style.display = 'block'
    }

    switch (status) {
      //* fresh = no messages, actions enabled
      case 'fresh': {
        break
      }
      //* pending = processing, no messages, actions disabled
      case 'pending': {
        if (hasButton) {
          this.buttonContentPendingTarget.style.display = 'block'
          this.buttonContentPayTarget.style.display = 'none'
        }
        break
      }
      //* error = error message, no completed message, actions enabled
      case 'error': {
        this.setFeedbackMessage(
          'error',
          error?.message || 'An error occurred while processing the payment'
        )
        break
      }
      //* completed = completed message, no error message, actions enabled
      // todo - disable button on completed
      case 'completed': {
        this.setFeedbackMessage('completed')

        break
      }
      default:
        break
    }
  }

  shouldAllowAttemptPayment() {
    if (this.component?.disable_button_until_page_valid && !this.isValidValue) return false
    return this.status !== 'pending' // && this.status !== 'completed'
  }

  updateCheckoutTotalInUserData(self: this, session: StripeCheckoutSession) {
    //* Update the checkout total in user data
    const checkoutTotalKey = self?.component?.save_outputs_to_user_data?.checkout_total
    const checkoutTotal = session?.total

    if (checkoutTotalKey) {
      self.dispatch('update', {
        detail: {
          key: checkoutTotalKey,
          value: checkoutTotal,
        },
      })
    }
  }

  //* Whether we should update the pay button text (only when payment element is shown)
  shouldUpdatePayButtonText(): boolean {
    const elements = this.component?.elements_to_display
    return Array.isArray(elements) && elements.includes('payment')
  }

  //* Sets the button text with the correct currency symbol and total price (or custom text)
  //* When language translation variants are configured for pay_button_text, the language +
  //* template controllers (wired in the generator) handle rendering — we defer to them to
  //* avoid overwriting translated text on Stripe session change events.
  setButtonText() {
    if (!this.checkout) throw new Error('Checkout is not initialized')
    const component = this.flowContext.component

    if (!this.hasButtonContentPayTextTarget)
      throw new Error('Button content pay text target is required')

    //* Only defer to the language + template controllers when translation variants are actually
    //* configured — legacy embeddables with a static pay_button_text keep the original path
    const hasPayButtonTranslations = Object.keys(component).some(
      (key) => key.startsWith('lang-') && key.endsWith('-pay_button_text')
    )
    if (component.display?.pay_button_text && hasPayButtonTranslations) return

    const stripeVersion = detectStripeVersion()
    const totalPrice =
      stripeVersion === 'basil'
        ? this.checkout.session().total.total.minorUnitsAmount / 100
        : (this.checkout.session().total.total as any) / 100
    const defaultText = `Pay ${getCurrencySymbol(
      component.checkout_session.currency
    )}${totalPrice.toFixed(2)}`

    this.buttonContentPayTextTarget.innerText = component.display?.pay_button_text
      ? computedValues(component.display?.pay_button_text, this.userDataValue)
      : component.checkout_session.mode === 'setup'
      ? `Save Card Details`
      : defaultText
  }

  //* Displays a completed or error message
  //* Set message to null to hide the container - a string or undefined will display the container
  setFeedbackMessage(type: 'completed' | 'error', message?: string | null, blocking?: boolean) {
    const container = type === 'completed' ? this.successContainerTarget : this.errorContainerTarget

    if (!container) return console.error(`Container target for ${type} message is required`)

    //* Remove any existing messages
    container?.querySelectorAll('div.message').forEach((el) => el.remove())

    //* Display the container if there is a message, otherwise hide it
    ;(container as HTMLElement).style.display = message !== null ? '' : 'none'

    //* If there is a message, add it to the container
    if (message) {
      const messageEl = document.createElement('div')
      messageEl.classList.add('message')
      messageEl.innerText = message
      container?.appendChild(messageEl)
    }
  }

  async saveOutputsToUserData() {
    return new Promise(async (resolve, reject) => {
      if (!this.checkout) throw new Error('Checkout is not initialized')
      if (!this.flowContext.flowId) return reject(new Error('Flow ID is required'))

      const response = (await callPaymentsBackend('get-checkout-session', {
        session_id: this.checkout.session().id,
        flow_id: this.flowContext.flowId,
        env: TEST_MODE ? 'dev' : 'prod',
      })) as StripeApiResponseGetCheckoutSession

      if (response.error)
        return reject(new Error(`Error creating checkout session: ${response.details}`))

      const checkoutSession = response.session

      if (!checkoutSession) return reject(new Error('No checkout session returned from backend'))

      try {
        //* Save the outputs to user data from the session
        await this.saveOutputsToUserDataFromSession(checkoutSession)
        resolve(void 0)
      } catch (e) {
        reject(e)
      }
    })
  }

  //* Save checkout session outputs to user data from a session object (e.g. from redirect or API).
  async saveOutputsToUserDataFromSession(
    checkoutSession: StripeCheckoutSessionFromApi
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.flowContext.flowId) return reject(new Error('Flow ID is required'))

      const updates = Object.entries(this.component.save_outputs_to_user_data || {}).reduce(
        (acc, [key, value]) => {
          const userDataKey = value
          const checkoutSessionKey = key?.replace('_id', '')
          const checkoutSessionValue = checkoutSession[checkoutSessionKey]

          if (!checkoutSessionKey || !checkoutSessionValue || typeof value !== 'string') {
            return acc
          }

          return {
            ...acc,
            [userDataKey]: checkoutSessionValue,
          }
        },
        {}
      )

      if (Object.keys(updates).length > 0) {
        console.log('Embeddables Stripe: Saving IDs to User Data:', updates)

        this.dispatch('update', {
          detail: {
            updates,
            callback: () => resolve(void 0),
          },
        })
      } else {
        console.log('Embeddables Stripe: No IDs found to save to User Data')
        resolve(void 0)
      }
    })
  }

  notifyEmbeddablesTeamOfError(err: {
    error: {
      message?: string
      code?: string
      type?: string
    }
    message?: string
  }) {
    console.error(err)
    this.setStatus('error', {
      code: err.error?.code || 'error',
      message: err.error?.message || err.message || '',
    })
    const errorType = err.error?.type
    if (errorType === 'validation_error' || errorType === 'card_error') return
    const slackMessage = `Stripe Error\n\ncode: ${(err.error && err.error.code) || 'n/a'}\ntype: ${
      errorType || err.message || JSON.stringify(err)
    }\nmessage: ${err.message || JSON.stringify(err)}\nerror:${JSON.stringify(err.error)}`
    const { message, code, type } = err?.error || {}
    const errObj = { message: message || '', code: code || '', type: type || '' }
    notify(slackMessage, 'C02QC92CGM8', undefined, errObj, this, window)
  }

  addEventLog(eventType: EventType) {
    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.flowContext.pageId) || 0
    const componentIndex =
      flow?.pages[pageIndex]?.components.findIndex(
        (component) => component.id === this.component.id
      ) || 0

    window.Savvy.addEventLogs?.([
      {
        flow_id: this.flowContext.flowId || '',
        event_type: eventType,
        timestamp: Date.now(),
        user_data_snapshot: this.userDataValue,
        data: {
          payment_service: 'stripe',
          component_id: this.component.id,
          component_key: this.component.key,
          component_path: [
            ...(this.flowContext.pageId ? ['pages', `${pageIndex}`] : []),
            'components',
            `${componentIndex}`,
          ],
        },
      },
    ])
  }
}

async function callPaymentsBackend(
  path: string,
  apidata: StripeApiRequest
): Promise<StripeApiResponse> {
  const url = `${getRendererBase()}/payments-v2/${path}`

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(apidata),
    headers: {
      'Content-Type': 'application/json',
    },
  })

  return (await response.json()) as StripeApiResponse
}

async function getStripe(self: Element) {
  if (!window.StripeInstance) {
    const publicKey = getPublicKey(self)
    window.StripeInstance = loadStripe(publicKey)
  }

  const stripe = await window.StripeInstance
  if (!stripe) throw new Error('Stripe failed to load')

  return stripe
}

function getPublicKey(self: Element) {
  const publicKeySpecified = self
    .closest('[data-controller~="flow"]')
    ?.getAttribute('data-flow-stripe-publishable-key-value')

  if (publicKeySpecified) return publicKeySpecified

  if (TEST_MODE) return STRIPE_TEST_PKEY

  throw new Error('Stripe publishable key is not available')
}

function missingRequiredHttps() {
  const isHttps = window.location.protocol === 'https:'

  return !TEST_MODE && !isHttps
}

function detectStripeVersion(): 'v3' | 'basil' {
  // Check if Stripe is loaded and has a version property
  if (typeof window.Stripe !== 'undefined' && (window.Stripe as any).version) {
    const version = (window.Stripe as any).version
    if (version === 3) {
      return 'v3'
    } else if (version === 'basil') {
      return 'basil'
    }
  }

  // Fallback: try to detect based on available methods
  if (typeof window.Stripe !== 'undefined') {
    // Check for Basil-specific methods
    if ((window.Stripe as any).initCheckout) {
      return 'basil'
    }
    // Check for v3-specific methods
    if ((window.Stripe as any).elements) {
      return 'v3'
    }
  }

  // Default to v3 for backward compatibility
  return 'v3'
}

function statusToAnalyticsEvent(status: StripeCheckoutStatus) {
  switch (status) {
    case 'completed':
      return 'payment:completed'
    case 'error':
      return 'payment:failed'
    case 'pending':
      return 'payment:attempted'
    default:
      throw new Error('Invalid status')
  }
}

function statusToActionKey(status: StripeCheckoutStatus) {
  switch (status) {
    case 'completed':
      return 'on_payment_complete_outputs'
    case 'error':
      return 'on_payment_failed_outputs'
    case 'pending':
      return 'on_payment_attempted_outputs'
    default:
      throw new Error('Invalid status')
  }
}

const cleanRedirectParams = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete('session_id')
  const newUrl = url.pathname + url.search + url.hash || window.location.pathname
  window.history.replaceState(null, '', newUrl)
}

const fetchSession = async ({
  sessionId,
  flowId,
}: {
  sessionId: string
  flowId: string
}): Promise<StripeApiResponseGetCheckoutSession> => {
  return (await callPaymentsBackend('get-checkout-session', {
    session_id: sessionId,
    flow_id: flowId,
    env: TEST_MODE ? 'dev' : 'prod',
  })) as StripeApiResponseGetCheckoutSession
}

//* Registry that tracks all active StripeCheckout instances ordered by their
//* position in the page's components array
type StripeCheckoutRegistryEntry = {
  componentId: string
  componentIndex: number
  checkout: StripeCheckout
}

const stripeCheckoutRegistry: StripeCheckoutRegistryEntry[] = []

function rebuildStripeCheckoutsOnWindow() {
  stripeCheckoutRegistry.sort((a, b) => a.componentIndex - b.componentIndex)
  window.StripeCheckouts = stripeCheckoutRegistry.map((e) => e.checkout)
}

function registerStripeCheckout(
  componentId: string,
  componentIndex: number,
  checkout: StripeCheckout
) {
  const existing = stripeCheckoutRegistry.findIndex((e) => e.componentId === componentId)
  if (existing >= 0) {
    stripeCheckoutRegistry[existing] = { componentId, componentIndex, checkout }
  } else {
    stripeCheckoutRegistry.push({ componentId, componentIndex, checkout })
  }
  rebuildStripeCheckoutsOnWindow()
}

function unregisterStripeCheckout(componentId: string) {
  const index = stripeCheckoutRegistry.findIndex((e) => e.componentId === componentId)
  if (index >= 0) {
    stripeCheckoutRegistry.splice(index, 1)
    rebuildStripeCheckoutsOnWindow()
  }
}

function getConfigWithInitialPropsOnly(config: StripeCheckout2) {
  return {
    ...config,
    //* The backend can use confirm_checkout_options.email to reuse an existing
    //* Stripe customer, so email changes must create a fresh Checkout Session.
    confirm_checkout_options: {
      email: config.confirm_checkout_options?.email || null,
    },
    display: null,
    save_outputs_to_user_data: null,
    on_payment_complete_outputs: null,
    on_payment_failed_outputs: null,
    on_payment_attempted_outputs: null,
    on_payment_button_click_outputs: null,
    on_payment_complete_conversions: null,
    next_on_complete: null,
    enforce_validation: null,
    disable_button_until_page_valid: null,
  }
}
